<template>
    <div class="base-textarea">
        <div
            :id="`${id}__container`"
            class="d-flex justify-content-md-between flex-column flex-md-row"
        >
            <div>
                <label
                    v-if="title"
                    :for="id"
                    >{{ title }}</label
                >
                <!-- Slot Label -->
                <div
                    v-else
                    class="form-group mb-0"
                >
                    <label :for="id">
                        <slot name="label"></slot>
                    </label>
                </div>
                <!-- End of Slot Label -->
            </div>

            <div class="btn-group">
                <button
                    v-for="item of langsList"
                    :id="`multilingual__${item.key}`"
                    :key="item.key"
                    type="button"
                    class="btn btn-sm text-uppercase"
                    :class="{ 'btn-primary': item.key === lang.key }"
                    @click="setLang(item)"
                >
                    {{ item.shortDisplay }}
                </button>
            </div>
        </div>
        <textarea
            :id="id"
            v-model="quillData"
            class="form-control"
            :class="inputClass"
            :placeholder="placeholder"
            :disabled="disabled"
        ></textarea>
        <span
            v-if="validation && validation.$error"
            class="invalid-feedback"
        >
            <slot
                name="error"
                :validation="validation"
            />
        </span>
    </div>
</template>

<script>
import { LANG_LIST } from '@common/constants';

export default {
    name: 'MultilingualTextarea',

    compatConfig: {
        MODE: 3,
    },

    props: {
        title: {
            type: String,
            default: '',
        },
        id: {
            type: [Number, String],
            default: '',
        },
        modelValue: {
            type: Object,
            required: true,
            default: () => ({}),
        },
        placeholderPrefix: {
            type: String,
            default: '',
        },
        inputClass: {
            type: [String, Object],
            default: '',
        },
        validation: {
            type: Object,
            nullable: true,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['update:modelValue'],

    data() {
        return {
            lang: {
                key: 'en',
                fullname: 'english',
            },
            langsList: LANG_LIST,
        };
    },

    computed: {
        quillData: {
            get() {
                if (this.modelValue && typeof this.modelValue === 'object' && this.modelValue.constructor === Object) {
                    return this.modelValue[this.lang.key];
                }

                return {};
            },
            set(value) {
                if (this.modelValue && typeof this.modelValue === 'object' && this.modelValue.constructor === Object) {
                    this.$emit('update:modelValue', {
                        ...this.modelValue,
                        [this.lang.key]: value,
                    });
                } else {
                    this.$emit('update:modelValue', {
                        [this.lang.key]: value,
                    });
                }
            },
        },
        placeholder() {
            const placeholder = this.placeholderPrefix || '';

            return this.$t(`${placeholder}`);
        },
    },

    mounted() {
        const langFromList = this.langsList.find((item) => item.key === this.$i18n.locale);
        this.lang = langFromList ?? this.lang;
    },

    methods: {
        setLang(lang) {
            this.lang = lang;
        },
    },
};
</script>

<style scoped lang="scss">
.base-textarea {
    textarea {
        background: $gray-200 !important;
        border-radius: 0.3rem;
        transition: border 0.3s ease-in-out;
        &:not(.is-invalid) {
            border: 2px solid transparent;
        }

        &:hover {
            background-color: $gray-300;
            border-color: $gray-400;
        }
    }

    label {
        font-size: 0.875rem;
        font-weight: normal;
    }
}
</style>
